import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import {
	performGetRequest,
	performPostRequest,
} from '@atlassian/jira-fetch/src/utils/requests.tsx';
import type { Action } from '@atlassian/react-sweet-state';
import { DEFAULT_PAGE_SIZE } from '../../../common/constants';
import type {
	ScreensServiceState,
	ScreensServiceContainerProps,
	GetScreensResponse,
	ScreensFilter,
	CreateScreenRequest,
	CreateScreenResponse,
} from '../types';
import { buildScreensUrl, buildCreateScreenUrl } from './utils';

export const defaultFilter: ScreensFilter = {
	pageNumber: 1,
	pageSize: DEFAULT_PAGE_SIZE,
	expand: ['screenScheme', 'workflowTransitions'],
	orderBy: 'name',
};

export const fetchScreensAsync = async (
	filter: ScreensFilter = defaultFilter,
): Promise<GetScreensResponse> => {
	const response = await performGetRequest(buildScreensUrl(filter));
	return response;
};

const fetchScreens =
	(
		filter: ScreensFilter = defaultFilter,
	): Action<ScreensServiceState, ScreensServiceContainerProps> =>
	async ({ setState, getState }) => {
		setState({
			screens: { ...getState().screens, isLoading: true },
			filter,
		});
		try {
			const response: GetScreensResponse = await fetchScreensAsync(filter);
			setState({
				screens: {
					values: response.values,
					maxResults: response.maxResults,
					startAt: response.startAt,
					total: response.total,
					isLast: response.isLast,
					isLoading: false,
					isError: false,
					error: undefined,
				},
				filter,
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			setState({
				screens: { ...getState().screens, isLoading: false, error, isError: true },
				filter,
			});
			fireErrorAnalytics({
				error,
				meta: {
					id: 'fetchScreens',
					packageName: 'jiraAdminScreensServices',
					teamName: 'morphinecake',
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	};

const createScreen =
	(
		createScreenRequest: CreateScreenRequest,
		onSuccess: (screenId: CreateScreenResponse) => void,
		onFailure: () => void,
	): Action<ScreensServiceState, ScreensServiceContainerProps> =>
	async ({ setState, getState, dispatch }) => {
		setState({
			screens: { ...getState().screens, isLoading: true },
		});
		try {
			const response: CreateScreenResponse = await performPostRequest(buildCreateScreenUrl(), {
				body: JSON.stringify({
					name: createScreenRequest.name,
					description: createScreenRequest.description,
				}),
			});
			dispatch(fetchScreens({ ...defaultFilter, ...getState().filter }));
			onSuccess(response);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				error,
				meta: {
					id: 'createScreen',
					packageName: 'jiraAdminScreensServices',
					teamName: 'morphinecake',
				},
				sendToPrivacyUnsafeSplunk: true,
			});
			onFailure();
		} finally {
			setState({
				screens: { ...getState().screens, isLoading: false },
			});
		}
	};
const actions = {
	fetchScreens,
	createScreen,
} as const;

export default actions;
